import { getAxiosClient } from '~src/api';
import Swal from 'sweetalert2';
import { changeImageSrc } from '~src/utils';
import { onErrorShowMessage } from '~src/errors';
import { getState } from '~src/state';
import { triggerStateChangedRequest } from '~core';
import { trackEvent } from '~src/tracking';
import { onCompleteAttachment } from '~uploader';
import { FEATURES, isFeatureEnabled } from '~src/features';
import { prepareImageForEditing } from '~core/image';
import ImageAnalyzer from './ImageAnalyzer';
import { showApprovalDialog } from '~core/ai/dialogs/showApprovalDialog';
import {
  startAiLoader,
  stopAiLoader,
  showAiSuccessMessage,
  AI_FEATURE_LOADING_MESSAGES,
} from '~core/aiLoader';
import { isPlaceholderEditing } from '~core/magicFrame';
import { checkAiCreditsBeforeUse } from '~core/aiCredits';

export default async function handleColorizeBw() {
  const allowUseColorizeBw = await isFeatureEnabled(
    FEATURES.AI_USE_COLORIZE_BW,
    false
  );
  if (!allowUseColorizeBw) {
    alert('Questa funzione non è disponibile nel tuo piano di abbonamento');
    return;
  }

  // Check credits before starting the operation
  const hasCredits = await checkAiCreditsBeforeUse({
    featureName: 'Colorize BW',
    requiredCredits: 1,
  });

  if (!hasCredits) {
    return;
  }

  let selection = window.canvas.getActiveObject();
  if (selection.type === 'MagicFrame') {
    selection = selection.editingImg;
  }

  if (isPlaceholderEditing(selection)) {
    return;
  }

  const grayscaleResult = ImageAnalyzer.isGrayscale(selection, {
    fastMode: false,
  });

  if (!grayscaleResult.isGrayscale) {
    // show swal to ask to confirm, because the image is already colorized
    const confirm = await Swal.fire({
      title: 'Attenzione!',
      text:
        'Colora serve per ridare il colore alle foto in bianco e nero. Questa foto sembra già essere a colori. Sei sicuro di voler proseguire?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#278e8e',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si, prosegui',
      cancelButtonText: 'Annulla',
    });

    if (!confirm.isConfirmed) {
      return;
    }
  }

  const imageUrl = await prepareImageForEditing(selection, false);

  if (!imageUrl) {
    console.error('No image URL found in current selection', selection);
    return;
  }

  const startTime = performance.now();

  startAiLoader(AI_FEATURE_LOADING_MESSAGES.COLORIZE_BW, '✨ Colora Foto');

  const httpClient = await getAxiosClient();

  const data = {
    imageUrl,
    resolution: 'full-hd',
  };

  let response;

  try {
    response = await httpClient.post(
      `${process.env.GIOTTO_API_URL}/colorize`,
      data
    );
  } catch (err) {
    console.error(err);
    stopAiLoader();
    return onErrorShowMessage();
  }

  stopAiLoader();

  // Show approval dialog with original and colorized images
  showApprovalDialog({
    images: response.data.imageUrl,
    onApprove: async (selectedImageUrl) => {
      const attachmentsUploader = getState('uploader.allegati');
      attachmentsUploader.cancelAll();

      await onCompleteAttachment({
        successful: [
          {
            name: 'ColorizeBW',
            uploadURL: selectedImageUrl,
            type: response.data.contentType,
            size: 0,
            customAttachmentProps: {
              colorizeBwRawImage: imageUrl,
              isColorizeBw: true,
            },
          },
        ],
      });

      let magicFrame = selection._magicFrame;
      if (selection.type === 'MagicFrame') {
        magicFrame = selection;
      }

      if (magicFrame) {
        await magicFrame.updatePerson(selectedImageUrl);
        magicFrame.scalePersonToWidth(magicFrame.width * magicFrame.scaleX);
        magicFrame.scalePersonToWidth(magicFrame.width * magicFrame.scaleX);
      } else {
        const currentProps = {
          scaleX: selection.scaleX,
          scaleY: selection.scaleY,
          top: selection.top,
          left: selection.left,
        };

        await changeImageSrc(selection, selectedImageUrl);

        selection.set({
          ...currentProps,
        });
      }
      window.canvas.renderAll();

      triggerStateChangedRequest();

      // Show success message
      showAiSuccessMessage(
        'Colorazione Completata',
        'La foto è stata colorata con successo!'
      );

      trackEvent('AI ColorizeBW', {
        success: true,
        time_taken: parseInt(performance.now() - startTime, 10),
      });
    },
  });
}
